import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "./adminApi";
import { toast } from "sonner";

const consentDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d) ? null : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const FILTERS = [
    { id: "all", label: "All" },
    { id: "qualified", label: "Qualified" },
    { id: "disqualified", label: "Disqualified" },
];

export default function AdminUsers() {
    const [data, setData] = useState({ items: [], count: 0, total: 0 });
    const [archetypes, setArchetypes] = useState({});
    const [filter, setFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const u = await adminApi.users(500, 0, statusFilter);
        const a = await adminApi.archetypes();
        setData(u);
        setArchetypes(Object.fromEntries(a.map(x => [x.id, x])));
        setLoading(false);
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const toggleUnlock = async (u) => {
        try {
            if (u.unlocked) {
                await adminApi.lockUser(u.session_id);
                toast.success("User locked");
            } else {
                await adminApi.unlockUser(u.session_id);
                toast.success("User unlocked");
            }
            await load();
        } catch {
            toast.error("Action failed");
        }
    };

    const downloadCsv = async () => {
        setExporting(true);
        try {
            await adminApi.exportUsersCsv(statusFilter);
            toast.success("CSV downloaded");
        } catch {
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    const filtered = data.items.filter(u =>
        !filter ||
        (u.session_id || "").toLowerCase().includes(filter.toLowerCase()) ||
        (u.referral_code || "").toLowerCase().includes(filter.toLowerCase()) ||
        (u.archetype_id || "").toLowerCase().includes(filter.toLowerCase()) ||
        (u.email || "").toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div className="p-10 max-w-6xl" data-testid="admin-users">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <span className="text-[10px] tracking-[0.36em] uppercase text-gold-deep">Operators</span>
                    <h1 className="mt-2 font-display text-5xl text-burgundy">
                        <span className="italic">Every</span> user
                    </h1>
                </div>
                <button
                    data-testid="export-csv-btn"
                    onClick={downloadCsv}
                    disabled={exporting}
                    className="bg-burgundy text-cream uppercase text-[10px] tracking-[0.24em] py-2.5 px-5 hover:bg-burgundy-deep transition-colors disabled:opacity-60"
                >
                    {exporting ? "Exporting…" : "Export CSV"}
                </button>
            </div>

            {/* Status segmented control */}
            <div className="mt-8 inline-flex border border-burgundy/15 bg-cream" data-testid="status-filter">
                {FILTERS.map(f => (
                    <button
                        key={f.id}
                        data-testid={`status-${f.id}`}
                        onClick={() => setStatusFilter(f.id)}
                        className={`px-5 py-2 text-[10px] tracking-[0.28em] uppercase border-r border-burgundy/15 last:border-r-0 transition-colors ${
                            statusFilter === f.id ? "bg-burgundy text-cream" : "text-ink hover:bg-burgundy/[0.05]"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="mt-6 flex items-center justify-between gap-4">
                <input
                    data-testid="user-filter"
                    type="text"
                    placeholder="Search session, code, archetype, or email…"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    className="w-80 bg-transparent border-b border-burgundy/30 focus:border-burgundy py-2 font-display text-lg text-burgundy focus:outline-none"
                />
                <div className="text-xs tracking-[0.28em] uppercase text-ink-muted">
                    {filtered.length} of {data.total}
                </div>
            </div>

            <div className="mt-6 border border-burgundy/15 bg-cream overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-cream-soft text-[10px] tracking-[0.28em] uppercase text-ink-muted">
                        <tr>
                            <th className="text-left px-4 py-3">Session</th>
                            <th className="text-left px-4 py-3">Email</th>
                            <th className="text-left px-4 py-3">Archetype</th>
                            <th className="text-left px-4 py-3">Code</th>
                            <th className="text-left px-4 py-3">Refs</th>
                            <th className="text-left px-4 py-3">Wrong</th>
                            <th className="text-left px-4 py-3">Consent</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-right px-4 py-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={9} className="px-4 py-10 text-center text-ink-muted">Loading…</td></tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr><td colSpan={9} className="px-4 py-10 text-center text-ink-muted">No users found.</td></tr>
                        )}
                        {filtered.map(u => (
                            <tr key={u.id} className="border-t border-burgundy/10" data-testid={`user-row-${u.session_id}`}>
                                <td className="px-4 py-3 font-mono text-[11px] text-ink">{u.session_id.slice(0, 14)}…</td>
                                <td className="px-4 py-3 text-xs text-ink">
                                    {u.email ? <span className="text-burgundy">{u.email}</span> : <span className="text-ink-light">—</span>}
                                </td>
                                <td className="px-4 py-3 font-display text-base text-burgundy">
                                    {u.disqualified ? (
                                        <span className="text-burgundy/70 italic text-sm">Disqualified ({u.disqualified_reason})</span>
                                    ) : (
                                        archetypes[u.archetype_id]?.name || <span className="text-ink-light">—</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 font-mono text-xs text-ink">{u.referral_code}</td>
                                <td className="px-4 py-3">{u.referral_count}</td>
                                <td className="px-4 py-3 text-xs">
                                    {u.screening_wrong_count != null ? (
                                        <span className={u.screening_wrong_count > 3 ? "text-burgundy font-medium" : "text-ink-muted"}>
                                            {u.screening_wrong_count}/10
                                        </span>
                                    ) : (
                                        <span className="text-ink-light">—</span>
                                    )}
                                </td>
                                <td className="px-4 py-3" data-testid={`consent-${u.session_id}`}>
                                    {u.consent_at ? (
                                        <div className="space-y-1">
                                            <div className="text-xs text-ink">{consentDate(u.consent_at)}</div>
                                            <div className="flex items-center gap-1.5">
                                                {u.consent_terms_version && (
                                                    <span className="text-[9px] tracking-[0.14em] uppercase px-1.5 py-0.5 bg-burgundy/10 text-ink-muted font-mono">
                                                        {u.consent_terms_version}
                                                    </span>
                                                )}
                                                {u.age_verified ? (
                                                    <span className="text-[9px] tracking-[0.14em] uppercase px-1.5 py-0.5 bg-gold/30 text-burgundy font-medium">
                                                        18+ ✓
                                                    </span>
                                                ) : (
                                                    <span className="text-[9px] tracking-[0.14em] uppercase px-1.5 py-0.5 bg-burgundy/10 text-ink-light">
                                                        No DOB
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="text-ink-light text-xs">—</span>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`text-[10px] tracking-[0.24em] uppercase px-2 py-1 ${u.unlocked ? "bg-gold/30 text-burgundy" : "bg-burgundy/10 text-ink-muted"}`}>
                                        {u.unlocked ? "Unlocked" : "Locked"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    {u.disqualified ? (
                                        <span className="text-[10px] tracking-[0.24em] uppercase text-ink-light">—</span>
                                    ) : (
                                        <button
                                            data-testid={`toggle-${u.session_id}`}
                                            onClick={() => toggleUnlock(u)}
                                            className="text-[10px] tracking-[0.24em] uppercase text-burgundy hover:underline underline-offset-4"
                                        >
                                            {u.unlocked ? "Lock" : "Unlock"}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
